import { formatDate } from '@angular/common';
import {
  EVENT_DEFAULT_IMAGE_PATH,
  EVENT_DESCRIPTION_FALLBACK,
  EVENT_DESCRIPTION_MAX_LENGTH,
  EVENT_LOCATION_FULL,
  EVENT_SITE_TITLE,
} from '@klubovna/shared-models';
import { PageMeta } from '@klubovna/shared-models';
import { EventItem } from '@klubovna/shared-models';

export interface EventMetaOptions {
  lang: 'sk' | 'en';
  baseUrl: string;
  staticContentUrl: string;
  defaultImageUrl?: string;
  fallbackDescription?: string;
  locationFull?: string;
  descriptionMaxLength?: number;
}

export function buildEventPageMeta(event: EventItem, options: EventMetaOptions): PageMeta {
  const fallbackDescription = options.fallbackDescription ?? EVENT_DESCRIPTION_FALLBACK;
  const locationFull = options.locationFull ?? EVENT_LOCATION_FULL;
  const descriptionMaxLength = options.descriptionMaxLength ?? EVENT_DESCRIPTION_MAX_LENGTH;
  const defaultImageUrl = options.defaultImageUrl ?? buildDefaultImageUrl(options.baseUrl);

  const title = getEventTitle(event, options.lang);
  const rawDescription = getEventDescription(event, options.lang, fallbackDescription);
  const sanitizedDescription = stripHtml(rawDescription) || fallbackDescription;
  const description = truncateDescription(sanitizedDescription, descriptionMaxLength);
  const dates = getEventDates(event, options.lang);
  const ogImage = resolveImageUrl(event.thumbnailPicture || event.titleImage, {
    defaultImageUrl,
    baseUrl: options.baseUrl,
    staticContentUrl: options.staticContentUrl,
  });
  const metaTags = buildMetaTags({
    ...dates,
    description,
    title,
    image: ogImage,
    location: locationFull,
  });

  return {
    title: `${ title } | ${ EVENT_SITE_TITLE }`,
    description,
    ogTitle: dates.formattedDate ? `${ title } · ${ dates.formattedDate }` : title,
    ogDescription: description,
    ogUrl: `${ trimTrailingSlash(options.baseUrl) }/event/${ event.slug }`,
    ogImage: ogImage,
    ogType: 'event',
    metaTags,
  };
}

export function stripHtml(content: string): string {
  if (!content) {
    return '';
  }

  const withoutTags = content.replace(/<[^>]+>/g, ' ');
  const decoded = decodeEntities(withoutTags);
  return decoded.replace(/\s+/g, ' ').trim();
}

function decodeEntities(text: string): string {
  return text
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, '\'')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&#x([\da-f]+);/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(parseInt(dec, 10)));
}

function getEventTitle(event: EventItem, lang: 'sk' | 'en'): string {
  return event.name[lang] || event.name['sk'] || event.name['en'] || EVENT_DESCRIPTION_FALLBACK;
}

function getEventDescription(event: EventItem, lang: 'sk' | 'en', fallback: string): string {
  if (lang === 'sk') {
    return event.description || event.detailText || fallback;
  }

  return event.descriptionEn || event.detailTextEn || event.description || fallback;
}

function getEventDates(
  event: EventItem,
  lang: 'sk' | 'en',
): { startISO?: string; endISO?: string; formattedDate?: string } {
  const startISO = event.date ? new Date(event.date).toISOString() : undefined;
  const endISO = event.endDate ? new Date(event.endDate).toISOString() : undefined;
  const formattedDate = event.date
    ? formatDate(event.date, 'd. MMMM y, HH:mm', lang === 'en' ? 'en-US' : 'sk-SK')
    : undefined;

  return { startISO, endISO, formattedDate };
}

function truncateDescription(text: string, maxLength: number): string {
  if (!text) {
    return EVENT_DESCRIPTION_FALLBACK;
  }

  return text.length > maxLength ? `${ text.slice(0, maxLength - 1) }…` : text;
}

function resolveImageUrl(
  imageUrl: string | null | undefined,
  options: { defaultImageUrl: string; baseUrl: string; staticContentUrl: string },
): string {
  const source = imageUrl?.trim();
  if (!source) {
    return options.defaultImageUrl;
  }

  if (/^https?:\/\//i.test(source)) {
    return source;
  }

  if (source.startsWith('//')) {
    return `https:${ source }`;
  }

  if (source.startsWith('/')) {
    return `${ trimTrailingSlash(options.baseUrl) }${ source }`;
  }

  const sanitized = source.replace(/^\/+/, '');
  const staticBase = trimTrailingSlash(options.staticContentUrl);
  return `${ staticBase }/${ sanitized }`;
}

function buildMetaTags({
                         startISO,
                         endISO,
                         formattedDate,
                         description,
                         title,
                         image,
                         location,
                       }: {
  startISO?: string;
  endISO?: string;
  formattedDate?: string;
  description: string;
  title: string;
  image: string;
  location: string;
}): Record<string, string> {
  const tags: Record<string, string> = {
    'event:location': location,
    'twitter:card': 'summary_large_image',
    'twitter:title': title,
    'twitter:description': description,
    'twitter:image': image,
  };

  if (startISO) {
    tags['event:start_time'] = startISO;
  }
  if (endISO) {
    tags['event:end_time'] = endISO;
  }
  if (formattedDate) {
    tags['event:datetime'] = formattedDate;
  }

  return tags;
}

function buildDefaultImageUrl(baseUrl: string): string {
  return `${ trimTrailingSlash(baseUrl) }${ EVENT_DEFAULT_IMAGE_PATH }`;
}

function trimTrailingSlash(value: string): string {
  return value.endsWith('/') ? value.slice(0, -1) : value;
}
